import { sessionDateOf } from './replayStats';
import { CTS_COLOR as PALETTE_CTS_COLOR } from './simulatorPalette';

/**
 * THE PHYSICS, DRAWN ON THE CHART: CTS marks, entry/exit verticals, and the live swing trendline.
 *
 * <h2>One series, because a second writer replaces the first</h2>
 *
 * Everything here lands on a single dedicated series, merged by `id`. The candlestick series
 * already carries the pattern feature's marks and the playhead has its own — attaching a third
 * writer to either would silently replace what was there, which this codebase has already paid
 * for twice. An own series also means the whole decoration disappears by dropping one id.
 *
 * <h2>markLine for all three line kinds, including the trendline</h2>
 *
 * `markLine` takes two shapes in one `data` array: `{xAxis}` draws a vertical spanning the grid,
 * and a `[{coord}, {coord}]` PAIR draws a segment between two points. So the verticals and the
 * sloping trendline share one series and need no `lines` series at all — which matters, because a
 * `lines` series defaults to the GEO coordinate system and on a chart with no geo component it
 * validates, renders NOTHING, and warns about nothing. That trap cost a day on the spiral arcs.
 *
 * <h2>NOTHING AFTER THE PLAYHEAD IS DRAWN</h2>
 *
 * Every annotation is filtered to bars at or before the current frame — `<= upToIndex` on each of
 * the four classes. A green exit line past the playhead would leak the answer the replay exists to
 * withhold, and in the most persuasive possible form: as a mark the engine supposedly made.
 *
 * <p>This filter is now the ONLY thing keeping the future off the chart. It used to have a
 * translucent veil beside it covering the bars after the playhead; that was removed on 2026-10-02
 * because the moving band flickered and obscured the price action. So the `upToIndex` bound is
 * load-bearing in a way it was not before — dropping it would no longer merely weaken a second
 * guard, it would draw tomorrow's exits today.
 */

export const REPLAY_ANNOTATION_OVERLAY_ID = 'dynaman-replay-annotations';
export const REPLAY_ANNOTATION_SERIES_ID = 'dynaman-replay-annotations-series';

/** Black dotted, per the brief: the bar that initiated a premise. */
export const ENTRY_COLOR = '#0b0f14';
/**
 * The entry vertical is the THICKEST line on the chart, deliberately.
 *
 * <p>It is the thing being looked for while scanning a replay, and at the exit's own width it was
 * one of three similar verticals. A dotted line also reads thinner than a solid one of the same
 * width, so matching the numbers would still have left it the faintest of the three.
 */
export const ENTRY_WIDTH = 5;
/** Exits and vectors. Thinner than the entry, so the entry wins the glance. */
export const EXIT_WIDTH = 2;
export const VECTOR_WIDTH = 2.5;
/** A profitable exit. */
export const WIN_EXIT_COLOR = '#2e7d32';
/** A loss-making exit. */
export const LOSS_EXIT_COLOR = '#c62828';
/**
 * CTS timing points — royal purple, from the palette.
 *
 * <p>Re-exported under its own name so existing importers are untouched. The value moved from
 * `#b388ff` to the palette's `#8B5CF6`: the same encoding, declared once, and now deliberately
 * the same purple the forward pillars use. Two shades of CTS on one chart would read as two
 * different things.
 */
export const CTS_COLOR = PALETTE_CTS_COLOR;
/** The live swing trendline the gate is proposing as a stop. */
export const TRENDLINE_COLOR = '#29b6f6';

/**
 * Session date of a frame, or null.
 *
 * <p>String-sliced through `sessionDateOf`, never through `new Date()`: an engine timestamp is an
 * offset date-time in the exchange's own offset, and converting via UTC moves a 19:30-05:00 bar
 * onto the NEXT candle. It survives a casual look because it only misfires on late sessions.
 */
const dateOfFrame = (frames, index) => {
  const frame = frames && frames[index];
  return frame ? sessionDateOf(frame.time) : null;
};

/**
 * Entry verticals: every bar that committed a premise, up to the playhead.
 *
 * <p>Taken from `timeline.breakpoints`, which the backend derives from the frames themselves —
 * so the lines cannot disagree with the bars the debugger pauses on.
 */
export function entryLines(timeline, upToIndex) {
  const frames = timeline?.frames || [];
  const stops = Array.isArray(timeline?.breakpoints) ? timeline.breakpoints : [];
  const codes = entrySignalsByDate(timeline);
  const dirs = entryDirectionsByDate(timeline);
  return stops
    .filter((i) => Number.isInteger(i) && i >= 0 && i <= upToIndex)
    .map((i) => dateOfFrame(frames, i))
    .filter(Boolean)
    .map((date) => ({
      xAxis: date,
      // COLOURED BY DIRECTION, not by outcome.
      //
      // Under the containment field the long and short breaches are two entirely different
      // populations -- 156 longs at PF 1.28 against 93 shorts at PF 0.42 -- so "which way did
      // this one go" is the first question of the audit, and reading it off a five-character
      // code is slower than seeing it. Colouring by PnL instead would answer a question the
      // reader can already see from where the exit marker sits.
      lineStyle: {
        color: entryColour(codes.get(date), dirs.get(date)),
        width: ENTRY_WIDTH,
        type: 'dotted',
        opacity: 0.95,
      },
      // THE RULE THAT TOOK THE TRADE, on the entry bar itself. For PTA/PTB that is the
      // five-letter key -- ARUT-B, APDT-S -- which is the whole point of a visual audit: a
      // reader checking whether a bounce was real needs to know which of the eight rules
      // claimed it, and reading that out of a ledger row three panels away is not the same
      // thing as seeing it on the bar.
      //
      // Absent for a breakout entry, which no provider labels, and absent rather than blank.
      label: codes.get(date)
        ? {
          show: true,
          formatter: codes.get(date),
          position: 'start',
          color: entryColour(codes.get(date), dirs.get(date)),
          fontSize: 10,
          fontWeight: 'bold',
          backgroundColor: 'rgba(255,255,255,0.75)',
          padding: [2, 3],
          borderRadius: 2,
        }
        : { show: false },
    }));
}

/** LONG and SHORT entry verticals, so the two breach populations are distinguishable at a glance. */
export const LONG_ENTRY_COLOR = '#2e7d32';
export const SHORT_ENTRY_COLOR = '#c62828';

/**
 * CONTAINMENT BREACH COLOURS, keyed by the middle token of the code.
 *
 * <h2>Why breach type outranks direction on a long-only run</h2>
 *
 * Direction colouring answered the question that mattered while PTA traded both sides. Long-only
 * makes every vertical a long, so colouring by direction paints the whole chart one colour and
 * says nothing. The question that matters now is WHICH breach, because the three are not one
 * population: `A-ESC-B` is 135 trades at PF 1.35 and +42.83% while `A-RUN-B` is 12 at PF 0.15 and
 * -10.08% -- and the forward-return test agrees, scoring the runaways -1.479 at 5 days against
 * the escapes' -0.170.
 *
 * <p>So an auditor stepping through the chart has to be able to tell a profitable escape from a
 * loss-making runaway without reading five characters on every marker.
 */
export const BREACH_COLORS = {
  ESC: '#2e7d32',   // escape velocity -- the one that works
  RUN: '#ef6c00',   // runaway -- PF 0.15, and visibly distinct from it
  FLP: '#1565c0',   // the traverse flip -- 1 trade, kept legible rather than hidden
};

/**
 * EQUATOR COLOURS, keyed by the WHOLE code rather than by its middle token.
 *
 * <h2>Both axes matter now, and direction is the bigger one</h2>
 *
 * Under the containment field PTA was long-only, so direction carried no information and the token
 * carried all of it. The equator runs BOTH SIDES, and the measured split is overwhelmingly
 * directional: `A-EQU-B` is 60 trades at PF 2.55 and +103.01% while `A-EQU-S` is 84 at PF 0.49 and
 * -41.78% -- the same trigger, the same geometry, opposite outcomes.
 *
 * <p>The trigger still matters second: `A-PER-B` is PF 0.84 against the midline long's 2.55, so a
 * fallback entry is not the same animal as a primary one even on the good side. Keying on the full
 * code is what encodes both at once; a token-only key would paint the profitable long and the
 * loss-making short identically, which is the exact failure long-only colouring had in reverse.
 */
export const SIGNAL_COLORS = {
  'A-EQU-B': '#2e7d32',   // the midline long -- PF 2.55, the best signal measured here
  'A-EQU-S': '#c62828',   // the midline short -- PF 0.49, and it must not look like the long
  'A-PER-B': '#9e9d24',   // the perimeter fallback, long -- PF 0.84
  'A-PER-S': '#6a1b9a',   // the perimeter fallback, short -- 1 trade, kept legible not hidden
};

/** `A-ESC-B` -> `ESC`, or null for anything that is not a containment code. */
export function breachOf(code) {
  const m = typeof code === 'string' ? code.match(/^[A-Z]-([A-Z]{3})-[BS]$/) : null;
  return m && BREACH_COLORS[m[1]] ? m[1] : null;
}

/**
 * The colour for an entry marker, most specific first: the whole equator code, then a containment
 * breach token, then direction.
 *
 * <p>All three tiers are live, because three entry vocabularies are reachable in this app. PTB and
 * CTS raise five-letter codes with no token at all and are correctly coloured by direction; a
 * containment control run still raises `A-ESC-B`; the equator raises the four above. One function
 * serves all of them so no run is left with uncoloured markers -- and a code nobody recognises
 * falls through to direction rather than to a colour borrowed from another vocabulary, which would
 * be a confident wrong answer where a vague one is harmless.
 */
export function entryColour(code, direction) {
  if (typeof code === 'string' && SIGNAL_COLORS[code]) return SIGNAL_COLORS[code];
  const breach = breachOf(code);
  return breach ? BREACH_COLORS[breach] : directionColour(direction);
}

/** The direction's colour, falling back to the neutral entry colour when it is unknown. */
export function directionColour(direction) {
  if (direction === 'LONG') return LONG_ENTRY_COLOR;
  if (direction === 'SHORT') return SHORT_ENTRY_COLOR;
  // A breakout entry, or a trade the timeline did not stamp. Neutral rather than guessed: a
  // wrongly coloured vertical is worse than an uncoloured one in an audit built on colour.
  return ENTRY_COLOR;
}

/**
 * Entry date -> the DIRECTION of the trade opened there.
 *
 * <p>Keyed by date for the same reason the codes are: a stamped trade's frame index is its EXIT
 * frame, while an entry vertical is drawn at the entry. Built from the identical source so a bar
 * can never show one trade's code beside another's colour.
 */
function entryDirectionsByDate(timeline) {
  const out = new Map();
  // THE SAME SOURCE entrySignalsByDate walks -- `timeline.trades`, not `transitions`. Reading a
  // different collection would be two answers to "which trade opened on this bar", and a bar
  // showing one trade's code beside another's colour is worse than showing neither.
  const stamped = Array.isArray(timeline?.trades) ? timeline.trades : [];
  stamped.forEach((s) => {
    const d = s?.trade?.direction;
    const t = s?.trade?.entryTime;
    if (d && t) out.set(sessionDateOf(t), d);
  });
  openTradesOf(timeline).forEach((open) => {
    if (open?.direction && open.entryTime) {
      out.set(sessionDateOf(open.entryTime), open.direction);
    }
  });
  return out;
}

/**
 * EVERY premise still held, as a list.
 *
 * <h2>Plural since Phase 12, with the singular as the fallback</h2>
 *
 * A concurrent book can hold three at once and each is an independent premise with its own entry
 * bar, its own entry price and its own code -- so drawing one of them would leave two entries
 * unmarked on a chart whose whole job is to show what the engine did. `openTrade` is still the
 * first of them on the wire and is read here for a timeline fetched before the plural existed,
 * which is a cached response rather than a hypothetical.
 */
function openTradesOf(timeline) {
  const many = timeline?.openTrades;
  if (Array.isArray(many) && many.length > 0) return many;
  return timeline?.openTrade ? [timeline.openTrade] : [];
}

/**
 * Entry date -> the five-letter code that opened the trade there.
 *
 * <p>Keyed by DATE rather than by frame index because a stamped trade's index is its EXIT frame,
 * while an entry line is drawn at the entry. The entry date is string-sliced from the engine's
 * own timestamp, never parsed through `Date` -- a `19:30-05:00` exit converted via UTC lands on
 * the NEXT candle, and only for late sessions, so it survives a casual look.
 *
 * <p>Includes EVERY trade regardless of the playhead, deliberately: the caller has already
 * filtered the entry lines it will draw, and a code can only ever be attached to a date that
 * survived that filter. Filtering twice would mean two places to get the bound wrong.
 */
function entrySignalsByDate(timeline) {
  const out = new Map();
  const stamped = Array.isArray(timeline?.trades) ? timeline.trades : [];
  stamped.forEach((s) => {
    const code = s?.trade?.entrySignal;
    const date = s?.trade?.entryTime ? sessionDateOf(s.trade.entryTime) : null;
    if (code && date) out.set(date, code);
  });
  openTradesOf(timeline).forEach((open) => {
    if (open?.entrySignal && open.entryTime) {
      // A still-running premise has no stamped row, and it is the one most likely to be under
      // inspection during a step-through. One per open tranche.
      out.set(sessionDateOf(open.entryTime), open.entrySignal);
    }
  });
  return out;
}

/**
 * Exit verticals: solid green on a profitable close, solid red on a losing one.
 *
 * <p>The sign is read from the DOLLAR result when the run is sized and from the percentage
 * otherwise, in that order. They agree on sign in every ordinary case; where they could differ is
 * a trade whose percentage is positive but whose costs took it negative, and there the dollar
 * figure is the one the account actually experienced.
 *
 * <p>A scratch — exactly zero — is drawn RED. It is not a profit, and `TradeRecord.isWin` applies
 * the same rule, so the chart cannot disagree with the ledger about what counted as a win.
 */
export function exitLines(timeline, upToIndex) {
  const frames = timeline?.frames || [];
  const stamped = Array.isArray(timeline?.trades) ? timeline.trades : [];
  return stamped
    .filter((s) => s && Number.isInteger(s.frameIndex)
      && s.frameIndex >= 0 && s.frameIndex <= upToIndex)
    .map((s) => {
      const date = dateOfFrame(frames, s.frameIndex);
      if (!date) return null;
      const won = wonTrade(s.trade);
      return {
        xAxis: date,
        lineStyle: {
          color: won ? WIN_EXIT_COLOR : LOSS_EXIT_COLOR,
          width: EXIT_WIDTH, type: 'solid', opacity: 0.95,
        },
      };
    })
    .filter(Boolean);
}

/**
 * Did this trade make money? Dollars first, percentage as a fallback.
 *
 * <p>One definition, used by both the exit verticals and the vectors. Two would eventually
 * disagree, and the symptom would be a red vertical at the end of a green vector — which reads as
 * a rendering bug rather than as duplicated logic.
 */
function wonTrade(trade) {
  const money = trade && Number.isFinite(trade.netPnl) ? trade.netPnl : null;
  const pct = trade && Number.isFinite(trade.pnlPercent) ? trade.pnlPercent : 0;
  return money !== null ? money > 0 : pct > 0;
}

/**
 * TRADE VECTORS: a diagonal from entry (date, price) to exit (date, price), one per closed trade.
 *
 * <h2>Why a vector says something the two verticals cannot</h2>
 *
 * A pair of verticals says WHEN a trade started and ended. The diagonal says what it DID — its
 * slope is the trajectory, its length is the holding period, and a glance across a chart full of
 * them shows whether the strategy is catching moves or being chopped. Two verticals a day apart
 * with a flat line between them look identical to two verticals a day apart with a 4% climb.
 *
 * <h2>Only CLOSED trades, and only once the playhead has reached the exit</h2>
 *
 * Drawing a vector for the open premise would require its exit, which has not happened — so it
 * would either be a line to nowhere or a line to an outcome the engine cannot know. Either is the
 * future arriving through the chart.
 *
 * <p>Both ends must be on the chart's axis: a trade whose entry predates the first drawn candle
 * yields nothing rather than a vector clamped to the left edge, which would misstate its slope
 * and its length at once.
 */
export function tradeVectors(timeline, dates, upToIndex) {
  const stamped = Array.isArray(timeline?.trades) ? timeline.trades : [];
  const axis = Array.isArray(dates) ? dates : [];
  if (axis.length === 0) return [];

  return stamped
    .filter((s) => s && Number.isInteger(s.frameIndex)
      && s.frameIndex >= 0 && s.frameIndex <= upToIndex && s.trade)
    .map((s) => {
      const t = s.trade;
      const from = sessionDateOf(t.entryTime);
      const to = sessionDateOf(t.exitTime);
      if (!from || !to) return null;
      if (axis.indexOf(from) < 0 || axis.indexOf(to) < 0) return null;
      if (!Number.isFinite(t.entryPrice) || !Number.isFinite(t.exitPrice)) return null;

      const colour = wonTrade(t) ? WIN_EXIT_COLOR : LOSS_EXIT_COLOR;
      return [
        {
          coord: [from, t.entryPrice],
          label: { show: false },
          lineStyle: { color: colour, width: VECTOR_WIDTH, type: 'solid', opacity: 0.9 },
        },
        { coord: [to, t.exitPrice], label: { show: false } },
      ];
    })
    .filter(Boolean);
}

/**
 * The live swing trendline as a two-point segment, or null.
 *
 * <p>Drawn from the OLDER pivot to the PROJECTION, not just between the two pivots: the segment a
 * reader needs to watch price test is the part extending past the newest pivot, and a line that
 * stopped at the second anchor would never reach the candles doing the testing.
 *
 * <p>Both ends must be on the chart's own axis. A pivot evicted from a bounded history window, or
 * a date the chart does not carry, yields null rather than a segment with one end clamped to an
 * edge — a trendline drawn to the wrong coordinate is worse than no trendline.
 */
export function trendlineSegment(trendline, dates) {
  if (!trendline) return null;
  const axis = Array.isArray(dates) ? dates : [];
  const from = sessionDateOf(trendline.firstTime);
  const to = sessionDateOf(trendline.atTime);
  if (!from || !to) return null;
  if (axis.indexOf(from) < 0 || axis.indexOf(to) < 0) return null;
  if (!Number.isFinite(trendline.firstPrice) || !Number.isFinite(trendline.atPrice)) return null;

  return [
    {
      coord: [from, trendline.firstPrice],
      // Only the far end is labelled, so the label sits where price is about to meet the line
      // rather than back at an anchor that is already history.
      label: { show: false },
      lineStyle: { color: TRENDLINE_COLOR, width: 2, type: 'solid', opacity: 0.95 },
    },
    {
      coord: [to, trendline.atPrice],
      label: {
        show: true,
        formatter: `trendline ${trendline.atPrice.toFixed(2)}`,
        position: 'end',
        color: TRENDLINE_COLOR,
        fontSize: 11,
        fontWeight: 'bold',
      },
    },
  ];
}

/**
 * CTS timing points as marks under their candles, up to the playhead.
 *
 * <p>Anchored to the bar's LOW with a downward offset, so a mark never sits on top of the body it
 * is annotating. Capped, because a 300-year CTS dataset marks thousands of sessions and ECharts
 * lays out every markPoint on every redraw — at a few hundred the replay stops being smooth,
 * which is the one thing the step-through cannot afford.
 */
export const MAX_CTS_MARKS = 240;

export function ctsMarks(timeline, upToIndex) {
  const frames = timeline?.frames || [];
  const marks = [];
  for (let i = 0; i <= Math.min(upToIndex, frames.length - 1); i++) {
    const frame = frames[i];
    if (!frame || !frame.diagnostics || !frame.diagnostics.ctsPoint) continue;
    const date = sessionDateOf(frame.time);
    if (!date) continue;
    marks.push({
      coord: [date, frame.low],
      symbol: 'triangle',
      symbolSize: 9,
      symbolOffset: [0, 14],
      itemStyle: { color: CTS_COLOR, opacity: 0.9 },
      label: { show: false },
    });
  }
  // The MOST RECENT ones, not the first: a reader stepping through a trade cares about the marks
  // near the playhead, and truncating from the front would drop exactly those.
  return marks.length > MAX_CTS_MARKS ? marks.slice(-MAX_CTS_MARKS) : marks;
}

/**
 * The whole annotation series, as ECharts wants it.
 *
 * <p>Empty `data` arrays rather than absent `markLine`/`markPoint` keys: a merge cannot REMOVE a
 * mark by omitting it, so a trendline that has gone away would otherwise stay frozen on the chart
 * for the rest of the replay. The playhead's own marks learned this the same way.
 */
export function annotationMarks(timeline, dates, upToIndex, trendline) {
  const verticals = [...entryLines(timeline, upToIndex), ...exitLines(timeline, upToIndex)];
  const vectors = tradeVectors(timeline, dates, upToIndex);
  const segment = trendlineSegment(trendline, dates);
  const data = [...verticals, ...vectors];
  if (segment) data.push(segment);
  return {
    markLine: {
      silent: true,
      symbol: 'none',
      animation: false,
      label: { show: false },
      data,
    },
    markPoint: {
      silent: true,
      animation: false,
      data: ctsMarks(timeline, upToIndex),
    },
  };
}
